import React from 'react';
import classNames from 'classnames';
import { Check, AlertTriangle } from 'react-feather';

import { Spinner } from 'ui';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import RetryButton from 'components/Practice/shared/RetryButton';
import UniversalPlayer from 'components/shared/UniversalPlayer';
import ToggleButton from 'components/shared/ToggleButton';

import {
  createRecordingElement,
  createPlayerElement,
  withZiggeo,
} from 'components/ui/ZiggeoVideo/ziggeoHelpers';
import ZiggeoMediaPlayer from 'components/ui/ZiggeoVideo/ZiggeoMediaPlayer';
import SkippedOption from 'components/Practice/Recording/SkippedOption';
import ReportTechnicalDifficultiesMessage from 'components/Practice/Recording/ReportTechnicalDifficultiesMessage';
import UniversalRecorder from 'components/shared/UniversalRecorder';
import UniversalUploaderComponent from 'components/shared/UniversalUploader/UniversalVideoUploader';
import trackEvent from 'misc/trackEvent';
import { isMobileDevice } from 'utils/browserCheck';
import { SCREEN_SIZES } from 'constants/config';

/*
  When ziggeoForAllMedia has been enabled for all customers & deprecated,
  this file can be removed, since it will no longer be used (initiated from
  VideoResponseQuestion in
  app/views/learning/questions/shared/_video_response.html.erb).
*/
class VideoEmbed extends React.Component {
  videoRecorderRef = React.createRef();

  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      showRecorder: true,
      isSeismicRecording: this.props.recordingRecord?.provider === 'seismic',
      isUVRReady: !this.props.seismicRecordingEnabled,
      windowSize: window.innerWidth,
      tryAgainDisabled: false,
    };
  }

  componentDidMount() {
    if (
      this.props.givenAnswer &&
      !this.props.hasZiggeoForAllMedia &&
      !this.state.isSeismicRecording
    ) {
      createPlayerElement(this.props.givenAnswer, this.props.questionId);
    }

    if (this.props.seismicRecordingEnabled) {
      window.addEventListener('resize', () => {
        this.setState({ windowSize: window.innerWidth });
      });
    }
  }

  componentDidUpdate(previousProps) {
    const {
      token,
      saveVideo,
      questionId,
      webcamPlaybackEnabled,
      recordWebcamEvent,
      setUploadFinished,
      setUploadStarted,
      handleBadUploadType,
      hasZiggeoForAllMedia,
    } = this.props;
    if (previousProps.token !== token && !hasZiggeoForAllMedia) {
      createRecordingElement({
        token,
        saveVideo,
        questionId,
        webcamPlaybackEnabled,
        recordWebcamEvent,
        setUploadingComplete: setUploadFinished,
        setUploadStarted,
        handleBadUploadType,
        allowRecord: !this.props.seismicRecordingEnabled,
        hideRecorder: this.toggleRecorder,
      });
      this.replaceHardcodedTranslations();
      return;
    }

    if (
      this.props.givenAnswer &&
      !this.props.hasZiggeoForAllMedia &&
      !this.state.isSeismicRecording
    ) {
      createPlayerElement(this.props.givenAnswer, this.props.questionId);
    }
  }

  replaceHardcodedTranslations() {
    const { current: videoRecorder } = this.videoRecorderRef;
    const recordButton = videoRecorder.querySelector(
      '.ba-videorecorder-chooser-button-0 > span',
    );
    if (recordButton) {
      recordButton.innerText = this.props.i18n.webcam_recording.record;
    }
    const uploadButton = !this.props.seismicRecordingEnabled
      ? videoRecorder.querySelector('.ba-videorecorder-chooser-button-1 > span')
      : videoRecorder.querySelector(
          '.ba-videorecorder-chooser-button-0 > span',
        );

    if (uploadButton) {
      uploadButton.innerText = this.props.i18n.webcam_recording.upload;
    }
    const uploadButtonInput = !this.props.seismicRecordingEnabled
      ? videoRecorder.querySelectorAll(
          '.ba-videorecorder-chooser-button-1 > input',
        )
      : videoRecorder.querySelectorAll(
          '.ba-videorecorder-chooser-button-0 > input',
        );

    if (uploadButtonInput && uploadButtonInput.length > 1) {
      uploadButtonInput[1].setAttribute(
        'aria-label',
        t('questions.question.webcam_recording.upload'),
      );
    }
  }

  tryAgainClicked = () => {
    this.props.loadRecording();
    trackEvent('practice/recording_try_again_clicked', {
      content_type: 'question',
      content_id: this.props.questionId,
    });
  };

  resetTryAgain = () => {
    this.setState({ tryAgainDisabled: false });
  };

  handleSeismicComplete = (storageId, format) => {
    this.setState({ isOpen: false, isSeismicRecording: true });

    if (storageId) {
      this.props.saveVideo(storageId, true, format);
    }
  };

  handleSeismicUploadComplete = data => {
    this.setState({ isSeismicRecording: true });
    if (data.token) {
      this.props.saveVideo(data.token, data.isSeismic, data.format);
    }
  };

  toggleRecorder = (showRecorder = false) => {
    this.setState({ showRecorder });
  };

  toggleUVRReady = () => {
    this.setState({ isUVRReady: true });
  };

  allowUVRRecord = () => {
    return !isMobileDevice() && this.state.windowSize > SCREEN_SIZES.SM;
  };

  render() {
    const {
      token,
      givenAnswer,
      errorMessage,
      i18n,
      skipRecording,
      finished,
      loadRecording,
      previewing,
      lessonTaking,
      questionId,
      progressId,
      removeAbilityToSkip,
      recordingRecord,
      hasZiggeoForAllMedia,
      apiKey,
      technicalDifficultiesReported,
      showAnswerAlreadySubmitted,
      sendFeedbackError,
      enabledAutomatedAnalysis,
      setEnabledAutomatedAnalysis,
      completedAt,
    } = this.props;
    const recorderStyles = classNames('VideoEmbed', {
      'VideoEmbed--disabled': finished && !givenAnswer,
    });
    const previewingStyles = classNames({ 'VideoEmbed--disabled': previewing });
    const playerStyles = classNames('VideoEmbed-player', {
      'VideoEmbed--background': !lessonTaking,
    });
    const showSkipOption = !finished && !removeAbilityToSkip;
    const showReportTechnicalDifficulties = removeAbilityToSkip;

    // This can be simplified once we are exclusively on the new skip experience
    const skipOrSendFeedbackOption = () => {
      if (
        !sendFeedbackError &&
        (showSkipOption || showReportTechnicalDifficulties)
      ) {
        return (
          <SkippedOption
            skipRecording={skipRecording}
            disabled={finished || previewing}
            showTechnicalDifficulties
            i18n={i18n}
          />
        );
      }
      return null;
    };

    if (givenAnswer) {
      return (
        <div>
          {this.props.seismicBiometricCompliance &&
            this.props.hasAutomatedAnalysis && (
              <ToggleButton
                questionId={questionId}
                progressId={progressId}
                enabledAutomatedAnalysis={enabledAutomatedAnalysis}
                setEnabledAutomatedAnalysis={setEnabledAutomatedAnalysis}
                disabled={!!completedAt}
              />
            )}
          {this.state.isSeismicRecording && (
            <UniversalPlayer
              id={recordingRecord.id}
              videoType="Recording"
              isPractice
            />
          )}
          {!this.state.isSeismicRecording &&
            (hasZiggeoForAllMedia ? (
              <ZiggeoMediaPlayer
                token={givenAnswer}
                apiKey={apiKey}
                className="maxh-600"
              />
            ) : (
              <div className={playerStyles}>
                <div className="VideoEmbed-videoPlayer" id={questionId} />
              </div>
            ))}
          {!finished && lessonTaking && (
            <div className="Video-redo d-f fw-w jc-sb ai-c pos-r">
              <Text
                className="d-f ai-c"
                variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD_SUCCESS}
              >
                <Check className="mr-4" />
                {i18n.webcam_recording.recording_complete}
              </Text>
              <RetryButton
                disabled={this.state.tryAgainDisabled}
                onClick={() => {
                  this.setState({ tryAgainDisabled: true });
                  this.toggleRecorder(true);
                  this.tryAgainClicked();
                }}
                setTryAgainDisabled={this.resetTryAgain}
              />
            </div>
          )}
          {showAnswerAlreadySubmitted && (
            <Text className="d-f ai-c pull-right" variant={TEXT_VARIANTS.BODY}>
              {t(
                'questions.question.media_recording.response_already_submitted',
              )}
            </Text>
          )}
        </div>
      );
    }

    return (
      <div className={recorderStyles}>
        <div className={previewingStyles}>
          {!technicalDifficultiesReported &&
            this.props.seismicBiometricCompliance &&
            this.props.hasAutomatedAnalysis && (
              <ToggleButton
                questionId={questionId}
                progressId={progressId}
                enabledAutomatedAnalysis={enabledAutomatedAnalysis}
                setEnabledAutomatedAnalysis={setEnabledAutomatedAnalysis}
                disabled={!!completedAt}
              />
            )}
          {!this.state.isUVRReady &&
            !token &&
            !previewing &&
            !technicalDifficultiesReported && (
              <div className="VideoEmbed--loading ta-c">
                <Spinner color="darker" />
                <span className="ml-16">{i18n.common.loading}</span>
              </div>
            )}
          {token && (
            <>
              <div
                className={classNames('Recorder', {
                  isSeismicRecording: this.props.seismicRecordingEnabled,
                  'pt-0': !this.state.showRecorder,
                })}
              >
                {this.props.seismicRecordingEnabled &&
                  this.state.showRecorder &&
                  this.allowUVRRecord() && (
                    <>
                      <button
                        type="button"
                        className="ba-videorecorder-chooser-button-0 CustomColor--borderColor"
                        onClick={() => this.setState({ isOpen: true })}
                        disabled={!this.state.isUVRReady}
                      >
                        {this.state.isUVRReady ? (
                          t('questions.question.webcam_recording.record')
                        ) : (
                          <Spinner />
                        )}
                      </button>
                      <UniversalRecorder
                        isOpen={this.state.isOpen}
                        handleComplete={this.handleSeismicComplete}
                        handleInitialize={() => {}}
                        handleRecordingCancel={() => {}}
                        handleRecordingStart={() => {}}
                        handleRecordingStop={() => {}}
                        onUVRReady={this.toggleUVRReady}
                      />
                    </>
                  )}
              </div>
              {this.props.seismicWebcamPracticeUploadEnabled && (
                <div
                  className={classNames(
                    {
                      'pos-a pl-12em mt-11em':
                        this.props.seismicWebcamPracticeUploadEnabled &&
                        !this.props.seismicRecordingEnabled,
                    },
                    'ZiggeoRecorderContainer minh-240 isSeismicRecording',
                  )}
                >
                  <div className="ZiggeoRecorder SeismicRecordingEnabled">
                    <label
                      htmlFor="upload"
                      className="EditorElement-uploadButton--universalVideoRecorder EditorElement-uploadButton mt-7 ta-c"
                    >
                      <UniversalUploaderComponent
                        seismicResponseUpload={
                          this.props.seismicWebcamPracticeUploadEnabled
                        }
                        accept=".video/quicktime, video/mp4, video/x-flv, video/*"
                        uploadBtnText={t(
                          'questions.question.webcam_recording.upload',
                        )}
                        onComplete={this.handleSeismicUploadComplete}
                      />
                    </label>
                  </div>
                </div>
              )}
              <div
                ref={this.videoRecorderRef}
                className={classNames('VideoEmbed-videoRecorder', {
                  'mb-128':
                    this.props.seismicWebcamPracticeUploadEnabled &&
                    this.props.seismicRecordingEnabled,
                  SeismicRecordingEnabled: this.props.seismicRecordingEnabled,
                  allowRecorder:
                    this.props.seismicWebcamPracticeUploadEnabled &&
                    !this.props.seismicRecordingEnabled,
                  hidden:
                    this.props.seismicWebcamPracticeUploadEnabled &&
                    this.props.seismicRecordingEnabled,
                })}
                id={questionId}
              />
            </>
          )}
          {previewing && (
            <div className="VideoEmbed-ziggeoButtonContainer d-f jc-c ai-c">
              <button
                type="button"
                className="ba-videorecorder-chooser-button-0"
              >
                {i18n.webcam_recording.record}
              </button>
              <button
                type="button"
                className="ba-videorecorder-chooser-button-1"
              >
                {i18n.webcam_recording.upload}
              </button>
            </div>
          )}
        </div>
        {skipOrSendFeedbackOption()}
        {(technicalDifficultiesReported || sendFeedbackError) && (
          <ReportTechnicalDifficultiesMessage
            reloadRecording={loadRecording}
            sendFeedbackError={sendFeedbackError}
          />
        )}
        {errorMessage && (
          <div className="ErrorContainer bg-transparent ff-ih d-f ai-c p-0 mt-8 mb-0 pos-r">
            <AlertTriangle className="mr-8" />
            <div className="f-1">
              <Text variant={TEXT_VARIANTS.INSTRUX_ERROR}>
                {`${t(
                  'questions.question.video_response.error',
                )} ${errorMessage}. `}
                {`${t('questions.question.video_response.error_follow_up')} ${t(
                  'common.or',
                )} `}
              </Text>
              <button
                type="button"
                className="u-linkBtn d-ib c-p"
                onClick={loadRecording}
              >
                <Text variant={TEXT_VARIANTS.BODY_LINK}>
                  {t('questions.question.video_response.try_again')}
                </Text>
              </button>
              .
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default withZiggeo(VideoEmbed);
